import { useDeferredValue, useState } from 'react';
import { Search, X } from 'lucide-react';
import { LancItem } from './LancItem';
import { LancFilter } from './LancFilter';
import { LANCFILTERS_DEFAULT, type Lancamento, type LancFilters } from './types';

interface LancSearchListProps {
  items: Lancamento[];
  isLoading: boolean;
  onOpen: (item: Lancamento) => void;
  /** Filtros iniciais (opcional) — usado quando o usuário chega via Home > Pendências. */
  initialFilters?: Partial<LancFilters>;
}

function LancSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="animate-pulse"
          style={{ height: 64, borderRadius: 'var(--r-3)', background: 'var(--card-soft)' }}
        />
      ))}
    </div>
  );
}

/**
 * SearchPill + LancFilter dropdown + lista filtrada de LancItems.
 * Busca local com debounce via useDeferredValue (zero setTimeout).
 */
export function LancSearchList({ items, isLoading, onOpen, initialFilters }: LancSearchListProps) {
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState<LancFilters>(() => ({
    ...LANCFILTERS_DEFAULT,
    ...initialFilters,
  }));
  const deferredQ = useDeferredValue(q);

  const cats = [...new Set(items.map((i) => i.cat))];

  const filtered = items.filter((it) => {
    const mq =
      !deferredQ || `${it.forn} ${it.cat}`.toLowerCase().includes(deferredQ.toLowerCase());
    const mt =
      filters.tipo === 'todos' ||
      (filters.tipo === 'ok' ? it.status === 'ok' : it.status !== 'ok');
    const mc = filters.cat === 'todas' || it.cat === filters.cat;
    return mq && mt && mc;
  });

  return (
    <>
      {/* Search + filtro */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            height: 44,
            padding: '0 6px 0 14px',
            background: 'var(--card-soft)',
            border: '1px solid var(--card-stroke)',
            borderRadius: 'var(--r-pill)',
          }}
        >
          <Search size={14} strokeWidth={1.7} color="var(--fg-muted)" aria-hidden />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar fornecedor, categoria…"
            aria-label="Buscar lançamentos"
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              fontSize: 13,
              color: 'var(--fg)',
              outline: 'none',
              fontFamily: 'var(--font-sans)',
            }}
          />
          {q && (
            <button
              onClick={() => setQ('')}
              aria-label="Limpar busca"
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: 'var(--card-stroke)',
                border: 'none',
                display: 'grid',
                placeItems: 'center',
                color: 'var(--fg-muted)',
                cursor: 'pointer',
                flexShrink: 0,
                minWidth: 44,
                minHeight: 44,
              }}
            >
              <X size={12} strokeWidth={2} aria-hidden />
            </button>
          )}
        </div>
        <LancFilter filters={filters} onChange={setFilters} cats={cats} />
      </div>

      {/* Lista */}
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              fontWeight: 600,
              color: 'var(--fg-muted)',
            }}
          >
            Recentes
          </div>
          <span
            style={{
              fontSize: 10.5,
              color: 'var(--fg-muted)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {filtered.length} {filtered.length === 1 ? 'item' : 'itens'}
          </span>
        </div>

        {isLoading ? (
          <LancSkeleton />
        ) : filtered.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map((it) => (
              <LancItem key={it.id} item={it} onOpen={onOpen} />
            ))}
          </div>
        ) : (
          <div
            style={{
              textAlign: 'center',
              padding: '28px 0',
              color: 'var(--fg-dim)',
              fontSize: 12.5,
            }}
          >
            Nenhum lançamento encontrado
          </div>
        )}
      </div>
    </>
  );
}
